/**
 * TZID -> IANA zone resolution for incoming iCalendar feeds - pure, Intl only.
 *
 * Every TZID in a feed is resolved ONCE at parse time to a zone this runtime can convert with
 * (Intl). Tiers, first hit wins:
 *
 *  1. A valid IANA name, case-insensitive, quotes and vendor prefixes stripped
 *     ("/mozilla.org/20050126_1/Europe/Stockholm"). UTC aliases -> "UTC".
 *  2. A Windows zone: CLDR windowsZones territory 001 ("W. Europe Standard Time" -> Europe/Berlin),
 *     an Outlook display label ("(UTC+01:00) Amsterdam, Berlin, ..."), or tzone://Microsoft/Utc.
 *     When the feed also carries a VTIMEZONE for that TZID and its offsets disagree with the mapped
 *     zone, tier 3's match wins (custom or stale definitions).
 *  3. The feed's own VTIMEZONE (TZID matched case-insensitively): its offsets are probed daily over
 *     this year and next (hourly around each change) and matched against IANA zones (the viewer's
 *     own zone first, then CLDR's golden zones, then every zone Intl knows). If the TZID is not
 *     declared but the feed has exactly one VTIMEZONE, that one is used, flagged uncertain.
 *  4. Fallback, flagged uncertain: the calendar's X-WR-TIMEZONE (resolved by tiers 1-3), else the
 *     viewer's own zone. Never UTC - an unknown zone shown at the viewer's wall clock is right far
 *     more often than one shifted by the viewer's whole offset.
 *
 * The VTIMEZONE's arithmetic is only used to IDENTIFY the zone; conversions always go through
 * Intl (ical.js gets DST edges wrong: ical.js #847).
 */

import { isValidTimeZone } from "@totodo/operations"
import * as rruleModule from "rrule"
import { WINDOWS_ZONE_IDS, WINDOWS_ZONE_LABELS } from "./windowsZones.generated"
import { zoneOffsetMs } from "./zonedTime"

// rrule CJS/ESM interop: handle both Vite (named exports) and Node ESM (default.RRule)
const RRule: typeof rruleModule.RRule =
  (rruleModule as unknown as { default?: { RRule: typeof rruleModule.RRule } })
    .default?.RRule ?? rruleModule.RRule

export type ZoneSource =
  | "iana"
  | "windows"
  | "vtimezone"
  | "calendar"
  | "user"

export interface ResolvedZone {
  /** A valid IANA zone ("UTC" for UTC). */
  zone: string
  source: ZoneSource
  /** True when the zone is a guess (tier 4, or an undeclared TZID matched to the only VTIMEZONE). */
  uncertain: boolean
}

export interface VTimezoneInfo {
  tzid: string
  /** The UTC offset (ms, east positive) the VTIMEZONE defines at instant `ms`, or null. */
  offsetAt(ms: number): number | null
}

export interface ResolveContext {
  /** The feed's VTIMEZONEs, keyed by `tzidKey(tzid)`. */
  vtimezones: ReadonlyMap<string, VTimezoneInfo>
  /** Raw X-WR-TIMEZONE of the calendar, if any. */
  calendarZone?: string | null
  /** The viewer's own IANA zone. */
  userZone: string
  /** "Now", for the probe years (tests). */
  now?: number
}

// ---------------------------------------------------------------------------
// Normalisation
// ---------------------------------------------------------------------------

/** Strip quotes and whitespace from a TZID as written. */
export function cleanTzid(raw: string): string {
  return raw.trim().replace(/^["']+|["']+$/g, "").trim()
}

/** Case- and whitespace-insensitive key for matching TZIDs to VTIMEZONEs. */
export function tzidKey(raw: string): string {
  return cleanTzid(raw).toLowerCase().replace(/\s+/g, " ")
}

const UTC_ALIASES = new Set([
  "utc",
  "gmt",
  "z",
  "etc/utc",
  "etc/gmt",
  "etc/uct",
  "etc/zulu",
  "etc/universal",
  "etc/greenwich",
  "uct",
  "universal",
  "zulu",
  "greenwich",
  "coordinated universal time",
  "tzone://microsoft/utc",
])

const canonicalCache = new Map<string, string | null>()

/** The canonical spelling of a valid IANA zone, or null. "UTC" for every UTC alias. */
export function canonicalIana(name: string): string | null {
  const cached = canonicalCache.get(name)
  if (cached !== undefined) return cached
  let result: string | null = null
  if (UTC_ALIASES.has(name.toLowerCase())) {
    result = "UTC"
  } else if (/^[A-Za-z]/.test(name) && isValidTimeZone(name)) {
    try {
      const resolved = new Intl.DateTimeFormat("en-US", { timeZone: name }).resolvedOptions().timeZone
      result = UTC_ALIASES.has(resolved.toLowerCase()) ? "UTC" : resolved
    } catch {
      result = null
    }
  }
  canonicalCache.set(name, result)
  return result
}

/** Tier 1: a valid IANA name, also behind vendor prefixes ("/mozilla.org/x/Europe/Berlin"). */
function resolveIana(tzid: string): string | null {
  const direct = canonicalIana(tzid)
  if (direct) return direct
  if (!tzid.includes("/")) return null
  const parts = tzid.split("/").filter(Boolean)
  for (let n = Math.min(3, parts.length); n >= 2; n--) {
    const candidate = canonicalIana(parts.slice(-n).join("/"))
    if (candidate) return candidate
  }
  return null
}

// ---------------------------------------------------------------------------
// Tier 2: Windows ids and display labels
// ---------------------------------------------------------------------------

let windowsIdIndex: Map<string, string> | null = null
let labelIndex: Map<string, string> | null = null
let labelCityIndex: Map<string, string> | null = null

/** "(GMT+01.00) Amsterdam,  Berlin" -> "(utc+01:00) amsterdam, berlin" */
function normalizeLabel(label: string): string {
  let s = label.toLowerCase().replace(/\s+/g, " ").trim()
  s = s.replace(/^\(\s*(?:gmt|utc)\s*([+-]\s*\d{1,2})[.:]?(\d{2})?\s*\)/, (_m, h: string, mm?: string) => {
    const sign = h.trim()[0]
    const hours = h.replace(/[^\d]/g, "").padStart(2, "0")
    return `(utc${sign}${hours}:${mm ?? "00"})`
  })
  s = s.replace(/^\(\s*(?:gmt|utc)\s*\)/, "(utc)")
  return s
}

function labelCities(normalized: string): string | null {
  const m = /^\([^)]*\)\s*(.+)$/.exec(normalized)
  return m?.[1] ? m[1].trim() : null
}

function buildWindowsIndexes() {
  windowsIdIndex = new Map()
  for (const [id, zone] of Object.entries(WINDOWS_ZONE_IDS)) windowsIdIndex.set(id.toLowerCase(), zone)
  labelIndex = new Map()
  labelCityIndex = new Map()
  for (const [label, zone] of Object.entries(WINDOWS_ZONE_LABELS)) {
    const n = normalizeLabel(label)
    labelIndex.set(n, zone)
    const cities = labelCities(n)
    if (cities && !labelCityIndex.has(cities)) labelCityIndex.set(cities, zone)
  }
}

/** Tier 2: a Windows zone id or display label -> IANA (canonical), or null. */
export function resolveWindowsZone(tzid: string): string | null {
  if (!windowsIdIndex) buildWindowsIndexes()
  const lower = tzid.toLowerCase().replace(/\s+/g, " ").trim()
  const byId = windowsIdIndex!.get(lower)
  if (byId) return canonicalIana(byId)
  if (lower.startsWith("(")) {
    const n = normalizeLabel(tzid)
    const byLabel = labelIndex!.get(n)
    if (byLabel) return canonicalIana(byLabel)
    const cities = labelCities(n)
    const byCities = cities ? labelCityIndex!.get(cities) : undefined
    if (byCities) return canonicalIana(byCities)
  }
  return null
}

/** Tiers 1-2 only: a TZID this runtime knows without looking at the feed. */
export function resolveKnownZone(raw: string): { zone: string; source: ZoneSource } | null {
  const tzid = cleanTzid(raw)
  if (!tzid) return null
  const iana = resolveIana(tzid)
  if (iana) return { zone: iana, source: "iana" }
  const win = resolveWindowsZone(tzid)
  if (win) return { zone: win, source: "windows" }
  return null
}

// ---------------------------------------------------------------------------
// Tier 3: VTIMEZONE offset matching
// ---------------------------------------------------------------------------

const HOUR = 3_600_000

/**
 * The VTIMEZONE's offsets at probe instants: 12:00 UTC every day of this year and next, plus every
 * hour across each day the offset changes - so two zones with the same DST dates but different
 * transition hours (America/New_York vs America/Havana) are told apart.
 */
function probeOffsets(info: VTimezoneInfo, now: number): { instants: number[]; offsets: number[] } | null {
  const year = new Date(now).getUTCFullYear()
  const first = Date.UTC(year, 0, 1, 12)
  const last = Date.UTC(year + 2, 0, 1, 12)
  const read = (ms: number): number | null => {
    try {
      const off = info.offsetAt(ms)
      return off === null || !Number.isFinite(off) ? null : off
    } catch {
      return null
    }
  }
  const instants: number[] = []
  const offsets: number[] = []
  let prev: number | null = null
  for (let t = first; t < last; t += 24 * HOUR) {
    const off = read(t)
    if (off === null) return null
    if (prev !== null && off !== prev) {
      for (let h = t - 23 * HOUR; h < t; h += HOUR) {
        const hourly = read(h)
        if (hourly === null) return null
        instants.push(h)
        offsets.push(hourly)
      }
    }
    instants.push(t)
    offsets.push(off)
    prev = off
  }
  return { instants, offsets }
}

/** Does `zone` have offset `offsets[i]` at every probe instant? */
function zoneMatches(zone: string, instants: number[], offsets: number[]): boolean {
  try {
    for (let i = 0; i < instants.length; i++) {
      if (zoneOffsetMs(instants[i]!, zone) !== offsets[i]) return false
    }
    return true
  } catch {
    return false
  }
}

/** One STANDARD or DAYLIGHT observance of a VTIMEZONE, as written. */
export interface VTimezoneObservance {
  /** DTSTART wall clock (local time before the change), pseudo-UTC ms. */
  start: number
  /** TZOFFSETFROM, ms. */
  offsetFrom: number
  /** TZOFFSETTO, ms. */
  offsetTo: number
  /** RRULE value, if any ("FREQ=YEARLY;BYMONTH=3;BYDAY=-1SU"). */
  rrule?: string
  /** RDATE wall clocks, pseudo-UTC ms. */
  rdates?: number[]
}

/**
 * A VTIMEZONE evaluated from its observances: each onset (DTSTART, RRULE and RDATE wall times) is
 * a change at `onset - TZOFFSETFROM`, after which TZOFFSETTO applies. Done here rather than with
 * ical.js, which misplaces the change by the zone's own offset.
 */
export function vtimezoneFromObservances(
  tzid: string,
  observances: readonly VTimezoneObservance[],
  now: number = Date.now(),
): VTimezoneInfo | null {
  if (observances.length === 0) return null
  const year = new Date(now).getUTCFullYear()
  const lo = Date.UTC(year - 2, 0, 1)
  const hi = Date.UTC(year + 4, 0, 1)
  const changes: { at: number; offset: number }[] = []
  for (const ob of observances) {
    const onsets: number[] = [ob.start]
    if (ob.rrule) {
      try {
        const options = RRule.parseString(ob.rrule)
        const rule = new RRule({ ...options, dtstart: new Date(ob.start), tzid: null })
        for (const d of rule.between(new Date(lo), new Date(hi), true)) onsets.push(d.getTime())
      } catch {
        // An unreadable rule contributes only its DTSTART
      }
    }
    if (ob.rdates) onsets.push(...ob.rdates)
    for (const wall of onsets) changes.push({ at: wall - ob.offsetFrom, offset: ob.offsetTo })
  }
  changes.sort((a, b) => a.at - b.at)
  const initial = observances.reduce((a, b) => (a.start <= b.start ? a : b)).offsetFrom
  return {
    tzid,
    offsetAt(ms: number) {
      let offset = initial
      for (const c of changes) {
        if (c.at > ms) break
        offset = c.offset
      }
      return offset
    },
  }
}

let allZonesCache: string[] | null = null

function allIanaZones(): string[] {
  if (allZonesCache) return allZonesCache
  const supported = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] })
    .supportedValuesOf
  let zones: string[] = []
  try {
    zones = supported ? supported("timeZone") : []
  } catch {
    zones = []
  }
  allZonesCache = zones
  return zones
}

let goldenZonesCache: string[] | null = null

function goldenZones(): string[] {
  if (!goldenZonesCache) goldenZonesCache = [...new Set(Object.values(WINDOWS_ZONE_IDS))]
  return goldenZonesCache
}

const vtzMatchCache = new Map<string, string | null>()

/**
 * Tier 3: the IANA zone whose offsets equal the VTIMEZONE's at every probe, or null.
 * `preferred` zones (a Windows-mapped hint, the viewer's zone) are tried first.
 */
export function matchVTimezone(
  info: VTimezoneInfo,
  preferred: readonly string[],
  now: number = Date.now(),
): string | null {
  const probes = probeOffsets(info, now)
  if (!probes) return null
  const { instants, offsets } = probes
  const signature = `${preferred.join(",")}|${instants[0]}|${offsets.join(",")}`
  const cached = vtzMatchCache.get(signature)
  if (cached !== undefined) return cached

  let found: string | null = null
  const tried = new Set<string>()
  const tryZone = (zone: string) => {
    if (found || tried.has(zone)) return
    tried.add(zone)
    if (zoneMatches(zone, instants, offsets)) found = canonicalIana(zone)
  }
  for (const z of preferred) tryZone(z)
  for (const z of goldenZones()) tryZone(z)
  for (const z of allIanaZones()) tryZone(z)
  if (!found && offsets.every((o) => o === offsets[0])) {
    // Fixed offset without a named match: Etc/GMT zones exist for whole hours (sign inverted).
    const hours = -offsets[0]! / 3_600_000
    if (Number.isInteger(hours) && Math.abs(hours) <= 14) {
      tryZone(hours === 0 ? "UTC" : `Etc/GMT${hours > 0 ? "+" : ""}${hours}`)
    }
  }
  vtzMatchCache.set(signature, found)
  return found
}

// ---------------------------------------------------------------------------
// The resolver
// ---------------------------------------------------------------------------

/**
 * Resolve one TZID as written in the feed (tiers 1-4). Use `createZoneResolver` to memoise per
 * feed.
 */
export function resolveTimeZone(raw: string, ctx: ResolveContext): ResolvedZone {
  const now = ctx.now ?? Date.now()
  const tzid = cleanTzid(raw)
  const vtz = tzid ? ctx.vtimezones.get(tzidKey(tzid)) : undefined

  // Tier 1
  const iana = tzid ? resolveIana(tzid) : null
  if (iana) return { zone: iana, source: "iana", uncertain: false }

  // Tier 2 (checked against the feed's own definition when there is one)
  const win = tzid ? resolveWindowsZone(tzid) : null
  if (win) {
    if (!vtz || win === "UTC") return { zone: win, source: "windows", uncertain: false }
    const matched = matchVTimezone(vtz, [win, ctx.userZone], now)
    if (!matched || matched === win) return { zone: win, source: "windows", uncertain: false }
    return { zone: matched, source: "vtimezone", uncertain: false }
  }

  // Tier 3
  if (vtz) {
    const matched = matchVTimezone(vtz, [ctx.userZone], now)
    if (matched) return { zone: matched, source: "vtimezone", uncertain: false }
  } else if (ctx.vtimezones.size === 1) {
    const only = ctx.vtimezones.values().next().value as VTimezoneInfo
    const known = resolveKnownZone(only.tzid)
    const matched = known?.zone ?? matchVTimezone(only, [ctx.userZone], now)
    if (matched) return { zone: matched, source: "vtimezone", uncertain: true }
  }

  // Tier 4
  return calendarFallback(ctx, now)
}

/** Tier 4: the calendar's X-WR-TIMEZONE, else the viewer's zone - always uncertain. */
export function calendarFallback(ctx: ResolveContext, now: number = ctx.now ?? Date.now()): ResolvedZone {
  const cal = resolveCalendarZone(ctx, now)
  if (cal) return { zone: cal, source: "calendar", uncertain: true }
  return { zone: canonicalIana(ctx.userZone) ?? "UTC", source: "user", uncertain: true }
}

/** X-WR-TIMEZONE resolved by tiers 1-3, or null. */
export function resolveCalendarZone(
  ctx: ResolveContext,
  now: number = ctx.now ?? Date.now(),
): string | null {
  if (!ctx.calendarZone) return null
  const tzid = cleanTzid(ctx.calendarZone)
  if (!tzid) return null
  const known = resolveKnownZone(tzid)
  if (known) return known.zone
  const vtz = ctx.vtimezones.get(tzidKey(tzid))
  return vtz ? matchVTimezone(vtz, [ctx.userZone], now) : null
}

/** A resolver for one feed, memoised per raw TZID. */
export function createZoneResolver(ctx: ResolveContext): (raw: string) => ResolvedZone {
  const memo = new Map<string, ResolvedZone>()
  return (raw: string) => {
    let hit = memo.get(raw)
    if (!hit) {
      hit = resolveTimeZone(raw, ctx)
      memo.set(raw, hit)
    }
    return hit
  }
}
